// client/src/components/ErrorBoundary.tsx
import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw } from "lucide-react";
import { onNavigation, shouldResetError } from "@/lib/navigationEvents";
import { boundaryErrorAction, isChunkLoadError, isChunkReloadPending, markChunkReloadPending, sessionStore, shouldReportBoundaryError } from "@/lib/chunkReloadGuard";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  eventId: string | null;
  /** the path the error happened on — navigating away clears the error (one broken page must not take the app down) */
  errorPath: string | null;
  /** a chunk reload is scheduled — render nothing until it happens (no error-screen flash) */
  reloading?: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, eventId: null, errorPath: null };
  }

  private unsubscribeNav: (() => void) | null = null;

  componentDidMount() {
    this.unsubscribeNav = onNavigation(this.onNavigate);
  }

  componentWillUnmount() {
    this.unsubscribeNav?.();
  }

  onNavigate = () => {
    if (shouldResetError(this.state.hasError, this.state.errorPath, window.location.pathname)) {
      this.setState({ hasError: false, eventId: null, errorPath: null });
    }
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // A chunk load error (bundle swapped under a live document by a deploy or an OTA): reload to pick
    // up the current chunk graph. Time-boxed loop guard (60 s) — the old once-per-session key was never
    // cleared, so every later OTA swap in the session showed this error screen (LAMIRAGECO-DV).
    const action = boundaryErrorAction(error, isChunkReloadPending(), sessionStore(), Date.now());
    if (action === "reload") {
      markChunkReloadPending();
      window.location.reload();
    }
    if (action !== "show") return { hasError: true, reloading: true };
    return { hasError: true, errorPath: typeof window !== "undefined" ? window.location.pathname : null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Skip Sentry noise: chunk errors, and anything thrown while a chunk reload is pending (React.lazy's
    // undefined-module error after a prevented preload).
    if (!shouldReportBoundaryError(error, isChunkReloadPending())) {
      if (isChunkLoadError(error)) console.warn("ChunkLoadError caught — reloading to pick up latest assets", error);
      return;
    }

    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
    this.setState({ eventId });

    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReport = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.reloading) return null;
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center bg-background">
          <AlertCircle className="h-16 w-16 text-destructive mb-4" />
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-muted-foreground mb-6 max-w-md">
            We apologize for the inconvenience. Our team has been notified and is working on a fix.
          </p>
          <div className="flex gap-4">
            <Button onClick={this.handleReload} variant="default">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reload Page
            </Button>
            {this.state.eventId && (
              <Button onClick={this.handleReport} variant="outline">
                Report Issue
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
